import { Injectable, Injector, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, throwError, of, map } from 'rxjs';
import { getStoredToken, setStoredToken, clearStoredToken } from './token-storage';
import { ChatSocketService } from './chat-socket.service';
import { bypassUnsavedChangesGuardOnce } from '../guards/unsaved-changes.guard';

interface AuthResponse {
  accessToken: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    plan: string;
    role: string;
    emailVerified: boolean;
    intendedPlan?: string | null;
  };
}

interface User {
  id: string;
  email: string;
  name: string | null;
  plan: string;
  role: string;
  emailVerified: boolean;
  // Non-null while Stripe is retrying a failed charge — shows the
  // "fix your payment method" banner in the dashboard layout.
  paymentFailedAt?: string | null;
  // True for comped accounts (plan set manually by a superadmin, no Stripe
  // customer behind it) — settings shows a "managed plan" state instead of
  // upgrade/portal buttons that would only 400.
  planOverride?: boolean;
  // Mirrored Stripe subscription state, for "renews on <date>" in settings.
  subscriptionStatus?: string | null;
  currentPeriodEnd?: string | null;
  stripeSubscriptionId?: string | null;
  // The plan they clicked on the pricing page at signup, if any — drives the
  // onboarding finish-screen upsell and the settings "pick up where you left
  // off" banner. Never touched after registration.
  intendedPlan?: string | null;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUser = signal<User | null>(null);
  readonly user = this.currentUser.asReadonly();
  readonly isAuthenticated = computed(() => !!this.currentUser());

  // Resolves once the initial token check (if any) has settled — a fresh
  // page load only has a token in localStorage, not a hydrated user yet,
  // so anything gating a route on isAuthenticated() must wait for this
  // first (see auth.guard.ts) or it reads a false "logged out" before the
  // /api/auth/me response has had a chance to arrive.
  private resolveReady!: () => void;
  private readonly ready = new Promise<void>((resolve) => {
    this.resolveReady = resolve;
  });

  // Router is resolved lazily via Injector rather than constructor-injected:
  // on a fresh page bootstrap, authGuard constructs AuthService for the
  // first time *during* the Router's own initial-navigation guard
  // resolution — injecting Router at construction time there closes a
  // circular DI cycle (NG0200), which loadUserFromToken()'s catchError then
  // misreads as an auth failure and wipes a perfectly valid token.
  constructor(
    private readonly http: HttpClient,
    private readonly injector: Injector,
  ) {
    this.loadUserFromToken();
  }

  whenReady(): Promise<void> {
    return this.ready;
  }

  // withCredentials so the browser stores the httpOnly refresh-token cookie the
  // server sets on these responses (and sends it back on /auth/refresh).
  register(data: { email: string; password: string; name?: string; intendedPlan?: string; captchaToken?: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/auth/register', data, { withCredentials: true }).pipe(
      tap((res) => this.handleAuthSuccess(res)),
      catchError((err) => throwError(() => err)),
    );
  }

  login(data: { email: string; password: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>('/api/auth/login', data, { withCredentials: true }).pipe(
      tap((res) => this.handleAuthSuccess(res)),
      catchError((err) => throwError(() => err)),
    );
  }

  /**
   * Exchange the refresh cookie for a fresh access token (used by the refresh
   * interceptor on a 401). Rotates the cookie server-side and stores the new
   * access token; emits it so the interceptor can retry the failed request.
   */
  refreshToken(): Observable<string> {
    return this.http.post<AuthResponse>('/api/auth/refresh', {}, { withCredentials: true }).pipe(
      tap((res) => {
        setStoredToken(res.accessToken);
        this.currentUser.set(res.user);
      }),
      map((res) => res.accessToken),
    );
  }

  forgotPassword(email: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/auth/forgot-password', { email });
  }

  resetPassword(token: string, newPassword: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/auth/reset-password', { token, newPassword });
  }

  verifyEmail(token: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/auth/verify-email', { token });
  }

  resendVerification(): Observable<{ message: string }> {
    return this.http.post<{ message: string }>('/api/auth/resend-verification', {});
  }

  /**
   * `forced` marks a logout the user didn't choose to trigger themselves —
   * an expired/invalid token discovered by the error interceptor or the
   * initial-load check below — as opposed to clicking a "Log out" button.
   * It skips the unsaved-changes confirm dialog on the resulting navigation,
   * since there's nothing meaningful for the user to "keep editing" toward.
   */
  logout(forced = false): void {
    if (forced) bypassUnsavedChangesGuardOnce();
    // Best-effort server-side revoke of the refresh token so a logged-out
    // session's cookie can't be rotated back into new access tokens. Never
    // blocks the local teardown/redirect.
    this.http.post('/api/auth/logout', {}, { withCredentials: true }).subscribe({ error: () => undefined });
    clearStoredToken();
    this.currentUser.set(null);
    this.injector.get(ChatSocketService).disconnect();
    this.injector.get(Router).navigate(['/login']);
  }

  getToken(): string | null {
    return getStoredToken();
  }

  /**
   * Applies an auth response obtained outside the normal login/register
   * flow — e.g. the auto-login token returned by accepting a team invite as
   * a brand-new user. Same session bookkeeping as handleAuthSuccess.
   */
  applySession(res: AuthResponse): void {
    this.handleAuthSuccess(res);
  }

  /** Re-fetches the current user (e.g. after a profile edit or plan change). */
  refreshUser(): void {
    this.http.get<User>('/api/auth/me').subscribe({
      next: (user) => this.currentUser.set(user),
      error: (err) => console.error('Failed to refresh user:', err),
    });
  }

  private handleAuthSuccess(res: AuthResponse): void {
    // Tear down any socket left over from a previous session in this tab —
    // it may still be authenticated as a different user (or the same user's
    // now-stale token) and connect()'s reuse-if-present short-circuit would
    // otherwise keep it around unauthenticated-as-current.
    this.injector.get(ChatSocketService).disconnect();
    setStoredToken(res.accessToken);
    this.currentUser.set(res.user);
  }

  private loadUserFromToken(): void {
    const token = this.getToken();
    if (!token) {
      this.resolveReady();
      return;
    }

    this.http.get<User>('/api/auth/me').pipe(
      catchError(() => {
        this.logout(true);
        return of(null);
      }),
    ).subscribe((user) => {
      if (user) this.currentUser.set(user);
      this.resolveReady();
    });
  }
}
